import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { getTeamLogoUrl, validateTeamLogo, TEAM_LOGO_ACCEPT } from '../../utils/formatters';
import { useAuth } from '../../contexts/AuthContext';
import { currentLocale } from '../../i18n';
import usePermissions from '../../hooks/usePermissions';
import LeaguePlayerPicker from './LeaguePlayerPicker';
import RegionBadge from './RegionBadge';
import CountrySelect from './CountrySelect';
import SteamFlag from '../common/SteamFlag';
import { fetchCountriesBulk } from '../../utils/countries';
import './Leagues.css';

/**
 * Map a roster role to a CSS modifier class for its badge.
 *
 * @param {string} role - Member role (captain/member/sub).
 * @returns {string} The modifier class suffix.
 */
function roleClass(role) {
  if (role === 'captain') return 'is-captain';
  if (role === 'sub') return 'is-sub';
  return 'is-member';
}

// Map a team-perspective result to a form-pill label key and CSS modifier. The
// single letters (V/F/O in Swedish) are localized, so we store a translation key
// resolved via t() at render — module-level constants can't call hooks. The keys
// are reused from the standings columns (colWins/colLosses/colDraws).
const RESULT_META = {
  win: { labelKey: 'leagues.colWins', cls: 'is-win' },
  loss: { labelKey: 'leagues.colLosses', cls: 'is-loss' },
  draw: { labelKey: 'leagues.colDraws', cls: 'is-draw' },
};

// Format options at module level; the formatter is built per render with
// currentLocale() so it reacts to a language change.
const WHEN_FMT_OPTS = {
  weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
};

/**
 * Team detail page: shows a persistent league team with its emblem and full
 * roster. Authenticated managers get inline controls to change the emblem, add
 * or remove members, and reassign the captain. Member rows link to profiles.
 */
export default function LeagueTeamDetail() {
  const { t } = useTranslation();
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, isLoggedIn, user } = useAuth();
  const { guildId, canManageLeagues } = usePermissions();

  const [team, setTeam] = useState(null);
  const [matches, setMatches] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manage, setManage] = useState(false);
  const [toAdd, setToAdd] = useState([]);
  const [addRole, setAddRole] = useState('member');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [editColor, setEditColor] = useState('#ff6b00');
  const [editDesc, setEditDesc] = useState('');
  const [editRegion, setEditRegion] = useState('');

  const fetchTeam = useCallback(() => {
    return fetch(`${API_URL}/api/leagues/teams/${teamId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        setTeam(data);
        if (data) {
          setEditColor(data.color || '#ff6b00');
          setEditDesc(data.description || '');
          setEditRegion(data.region || '');
          // Resolve roster flags by steamid64 (shared country cache).
          fetchCountriesBulk((data.members || []).map((m) => m.steamid64).filter(Boolean));
        }
      })
      .catch(() => setTeam(null));
  }, [teamId]);

  const fetchMatches = useCallback(() => {
    return fetch(`${API_URL}/api/leagues/teams/${teamId}/matches`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setMatches(Array.isArray(data) ? data : []))
      .catch(() => setMatches([]));
  }, [teamId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchTeam(), fetchMatches()]).finally(() => setLoading(false));
  }, [fetchTeam, fetchMatches]);

  const jsonHeaders = useCallback(
    () => ({ ...getAuthHeaders(), 'Content-Type': 'application/json' }),
    [getAuthHeaders],
  );

  // Pending invites are only fetched while managing (captain/admin endpoint).
  const fetchInvites = useCallback(() => {
    return fetch(`${API_URL}/api/leagues/teams/${teamId}/invites`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setInvites(Array.isArray(data) ? data : []))
      .catch(() => setInvites([]));
  }, [teamId, getAuthHeaders]);

  const setCaptain = async (playerId) => {
    setBusy(true);
    await fetch(`${API_URL}/api/leagues/teams/${teamId}/captain`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ player_id: playerId }),
    });
    await fetchTeam();
    setBusy(false);
  };

  const removeMember = async (playerId) => {
    setBusy(true);
    await fetch(`${API_URL}/api/leagues/teams/${teamId}/members/${playerId}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    await fetchTeam();
    setBusy(false);
  };

  const inviteMembers = async () => {
    setBusy(true);
    setError('');
    let lastError = '';
    for (const p of toAdd) {
      const resp = await fetch(`${API_URL}/api/leagues/teams/${teamId}/invites`, {
        method: 'POST', headers: jsonHeaders(),
        body: JSON.stringify({ player_id: p.player_id, role: addRole }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        lastError = data.error || t('leagues.inviteFailed');
      }
    }
    setToAdd([]);
    await fetchInvites();
    if (lastError) setError(lastError);
    setBusy(false);
  };

  const cancelInvite = async (inviteId) => {
    setBusy(true);
    await fetch(`${API_URL}/api/leagues/invites/${inviteId}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    await fetchInvites();
    setBusy(false);
  };

  const disband = async () => {
    if (!window.confirm(t('leagues.disbandConfirm'))) return;
    setBusy(true);
    await fetch(`${API_URL}/api/leagues/teams/${teamId}/disband`, {
      method: 'POST', headers: getAuthHeaders(),
    });
    await fetchTeam();
    setBusy(false);
  };

  const reactivate = async () => {
    setBusy(true);
    setError('');
    const resp = await fetch(`${API_URL}/api/leagues/teams/${teamId}`, {
      method: 'PATCH', headers: jsonHeaders(), body: JSON.stringify({ is_active: true }),
    });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      setError(data.error || t('leagues.reactivateFailed'));
    }
    await fetchTeam();
    setBusy(false);
  };

  const changeLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const logoError = validateTeamLogo(file);
    if (logoError) {
      setError(logoError);
      e.target.value = '';
      return;
    }
    setError('');
    setBusy(true);
    const fd = new FormData();
    fd.append('logo', file);
    const resp = await fetch(`${API_URL}/api/leagues/teams/${teamId}/logo`, {
      method: 'POST', headers: getAuthHeaders(), body: fd,
    });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      setError(data.error || t('leagues.logoUploadFailed'));
      setBusy(false);
      e.target.value = '';
      return;
    }
    await fetchTeam();
    setBusy(false);
  };

  const saveDetails = async () => {
    setBusy(true);
    await fetch(`${API_URL}/api/leagues/teams/${teamId}`, {
      method: 'PATCH', headers: jsonHeaders(),
      body: JSON.stringify({ color: editColor, description: editDesc.trim(), region: editRegion.trim() }),
    });
    await fetchTeam();
    setBusy(false);
  };

  // Load pending invites when the manage panel opens (captain/admin only).
  useEffect(() => {
    if (manage) fetchInvites();
  }, [manage, fetchInvites]);

  const toggleAdd = (player) => {
    setToAdd((prev) =>
      prev.some((p) => p.player_id === player.player_id)
        ? prev.filter((p) => p.player_id !== player.player_id)
        : [...prev, player],
    );
  };

  if (loading) {
    return <div className="loading-screen"><div className="loader" /></div>;
  }
  if (!team) {
    return <div className="league-empty">{t('leagues.teamNotFound')}</div>;
  }

  const logo = getTeamLogoUrl(team.logo_url);
  const memberIds = team.members.map((m) => m.player_id);
  // Captains manage their own team; league admins (league.manage) manage any
  // team. Must mirror the backend's _can_manage_team gate — NOT match.create.
  const isCaptain = Boolean(
    user?.player_id && team.captain_id && String(user.player_id) === String(team.captain_id),
  );
  const canManage = isLoggedIn && (canManageLeagues || isCaptain);
  // Form streak: the most recent recorded results (matches arrive newest-first).
  const form = matches.filter((m) => m.team_result).slice(0, 6).map((m) => m.team_result);
  // Split fixtures: upcoming (booked time first, then undated) vs played history.
  const upcoming = matches
    .filter((m) => !m.points_awarded)
    .slice()
    .sort((a, b) => {
      if (a.scheduled_at && b.scheduled_at) return new Date(a.scheduled_at) - new Date(b.scheduled_at);
      if (a.scheduled_at) return -1;
      if (b.scheduled_at) return 1;
      return 0;
    });
  const playedMatches = matches.filter((m) => m.points_awarded);
  const whenFmt = new Intl.DateTimeFormat(currentLocale(), WHEN_FMT_OPTS);

  return (
    <div className="league-page">
      <div className="league-detail-header">
        <button className="league-back-btn" onClick={() => navigate('/leagues/teams')}>← {t('createMatch.teams')}</button>
        <div
          className="team-detail-identity"
          style={{ '--team-color': team.color || 'var(--accent-orange)' }}
        >
          <span className="team-color-bar" />
          {logo ? (
            <img className="team-logo team-logo-lg" src={logo} alt="" />
          ) : (
            <span className="team-logo team-logo-lg team-logo-blank">{(team.tag || team.name || '?')[0]}</span>
          )}
          <div className="league-detail-titles">
            {team.tag && <span className="team-tag" title={t('leagues.teamTagTitle')}>{team.tag}</span>}
            <h1 className="league-page-title">{team.name}</h1>
            <div className="team-detail-badges">
              <RegionBadge region={team.region} className="team-region-lg" />
              <span className="league-mode-badge">{team.game_mode_display}</span>
              {!team.is_active && <span className="team-inactive-badge">{t('leagues.disbanded')}</span>}
            </div>
          </div>
        </div>
        {canManage && (
          <button className="league-back-btn" onClick={() => setManage((m) => !m)}>
            {manage ? t('leagues.manageDone') : t('leagues.manage')}
          </button>
        )}
      </div>

      {team.description && !manage && <p className="team-description">{team.description}</p>}

      {manage && (
        <div className="team-edit-panel">
          <label className="form-field team-logo-change">
            <span className="form-label">{t('leagues.changeEmblem')}</span>
            <input type="file" accept={TEAM_LOGO_ACCEPT} onChange={changeLogo} disabled={busy} />
          </label>
          <div className="form-row">
            <label className="form-field form-field-color">
              <span className="form-label">{t('leagues.teamColor')}</span>
              <input className="form-color" type="color" value={editColor} onChange={(e) => setEditColor(e.target.value)} />
            </label>
            <label className="form-field">
              <span className="form-label">{t('leagues.countryOptional')}</span>
              <CountrySelect value={editRegion} onChange={setEditRegion} />
            </label>
            <label className="form-field">
              <span className="form-label">{t('leagues.motto')}</span>
              <textarea className="form-input form-textarea" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} maxLength={280} rows={2} />
            </label>
          </div>
          <button className="form-submit form-submit-sm" disabled={busy} onClick={saveDetails}>{t('settings.save')}</button>
          {error && <p className="form-error">{error}</p>}
          <div className="team-danger-row">
            {team.is_active ? (
              <button className="roster-btn roster-btn-danger team-disband-btn" disabled={busy} onClick={disband}>
                {t('leagues.disband')}
              </button>
            ) : (
              <button className="form-submit form-submit-sm" disabled={busy} onClick={reactivate}>
                {t('leagues.reactivate')}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="team-card notched-card team-detail-card">
        <h3 className="division-title">{t('leagues.rosterCount', { count: team.member_count })}</h3>
        <ul className="team-roster">
          {team.members.map((m) => {
            const clickable = Boolean(m.steamid64);
            return (
              <li key={m.player_id} className="roster-row">
                <span className={`roster-role ${roleClass(m.role)}`}>{m.role_display}</span>
                <span
                  className={`roster-name ${clickable ? 'roster-name-link' : ''}`}
                  onClick={clickable ? () => navigate(`/stats/players/${m.steamid64}`) : undefined}
                >
                  <SteamFlag steamid64={m.steamid64} className="roster-flag" />
                  {m.display_name}
                </span>
                {manage && (
                  <span className="roster-actions">
                    {m.role !== 'captain' && (
                      <button className="roster-btn" title={t('leagues.makeCaptain')} disabled={busy} onClick={() => setCaptain(m.player_id)}>★</button>
                    )}
                    <button className="roster-btn roster-btn-danger" title={t('comments.delete')} disabled={busy} onClick={() => removeMember(m.player_id)}>×</button>
                  </span>
                )}
              </li>
            );
          })}
          {team.members.length === 0 && <li className="roster-row roster-empty">{t('leagues.noMembers')}</li>}
        </ul>

        {manage && (
          <div className="roster-add">
            {invites.length > 0 && (
              <ul className="invite-pending-list">
                {invites.map((inv) => (
                  <li key={inv.id} className="invite-pending-row">
                    <span className="invite-pending-name">{inv.player_name}</span>
                    <span className="invite-pending-meta">{t('leagues.invitePending', { role: inv.role_display?.toLowerCase() })}</span>
                    <button
                      className="roster-btn roster-btn-danger"
                      title={t('leagues.revokeInvite')}
                      disabled={busy}
                      onClick={() => cancelInvite(inv.id)}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="roster-add-head">
              <span className="form-label">{t('leagues.invitePlayer')}</span>
              <select className="form-input form-input-sm" value={addRole} onChange={(e) => setAddRole(e.target.value)}>
                <option value="member">{t('leagues.roleMember')}</option>
                <option value="sub">{t('leagues.roleSub')}</option>
              </select>
              <button className="form-submit form-submit-sm" disabled={busy || toAdd.length === 0} onClick={inviteMembers}>
                {t('leagues.inviteCount', { count: toAdd.length })}
              </button>
            </div>
            <p className="form-hint">{t('leagues.inviteHint')}</p>
            <LeaguePlayerPicker guildId={guildId} selected={toAdd} onToggle={toggleAdd} excludeIds={memberIds} />
          </div>
        )}
      </div>

      {upcoming.length > 0 && (
        <div className="team-card notched-card team-detail-card">
          <h3 className="division-title">{t('leagues.upcomingCount', { count: upcoming.length })}</h3>
          <ul className="team-match-list">
            {upcoming.map((m) => {
              const opponent = m.team_side === 'home' ? m.away_name : m.home_name;
              const isHome = m.team_side === 'home';
              return (
                <li
                  key={m.id}
                  className={`team-match-row ${m.match_id ? 'is-clickable' : ''}`}
                  onClick={m.match_id ? () => navigate(`/stats/matches/${m.match_id}`) : undefined}
                >
                  <span
                    className={`team-match-side ${isHome ? 'is-home' : 'is-away'}`}
                    title={isHome ? t('leagues.homeTitle') : t('leagues.awayTitle')}
                  >
                    {isHome ? '🏠' : '✈️'}
                  </span>
                  <span className="team-match-opponent">{opponent}</span>
                  <span className="team-match-score">
                    {m.scheduled_at ? whenFmt.format(new Date(m.scheduled_at)) : t('leagues.notScheduled')}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {playedMatches.length > 0 && (
        <div className="team-card notched-card team-detail-card">
          <div className="team-history-head">
            <h3 className="division-title">{t('leagues.playedCount', { count: playedMatches.length })}</h3>
            {form.length > 0 && (
              <div className="team-form" title={t('leagues.recentResults')}>
                {form.map((r, i) => (
                  <span key={i} className={`form-pill ${RESULT_META[r].cls}`}>{t(RESULT_META[r].labelKey)}</span>
                ))}
              </div>
            )}
          </div>
          <ul className="team-match-list">
            {playedMatches.map((m) => {
              const opponent = m.team_side === 'home' ? m.away_name : m.home_name;
              const own = m.team_side === 'home' ? m.home_score : m.away_score;
              const opp = m.team_side === 'home' ? m.away_score : m.home_score;
              const meta = m.team_result ? RESULT_META[m.team_result] : null;
              return (
                <li
                  key={m.id}
                  className={`team-match-row ${m.match_id ? 'is-clickable' : ''}`}
                  onClick={m.match_id ? () => navigate(`/stats/matches/${m.match_id}`) : undefined}
                >
                  <span className={`team-match-result ${meta ? meta.cls : 'is-pending'}`}>
                    {meta ? t(meta.labelKey) : '–'}
                  </span>
                  <span className="team-match-opponent">{opponent}</span>
                  <span className="team-match-score">
                    {m.points_awarded ? `${own ?? 0}–${opp ?? 0}` : (m.status_display || m.status)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
